import React, { useRef, useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const products = [
  {
    id: 1,
    name: { pt: 'Café', en: 'Coffee', es: 'Café' },
    image: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=400&q=80',
    tons: '2.500t',
  },
  {
    id: 2,
    name: { pt: 'Vinhos', en: 'Wines', es: 'Vinos' },
    image: 'https://images.unsplash.com/photo-1474722883albertrt-849391511?w=400&q=80',
    fallbackImage: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&q=80',
    tons: '1.800t',
  },
  {
    id: 3,
    name: { pt: 'Granito', en: 'Granite', es: 'Granito' },
    image: 'https://images.pexels.com/photos/19302430/pexels-photo-19302430.jpeg?auto=compress&cs=tinysrgb&w=400',
    tons: '5.200t',
  },
  {
    id: 4,
    name: { pt: 'Carnes', en: 'Meat', es: 'Carnes' },
    image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400&q=80',
    tons: '3.100t',
  },
  {
    id: 5,
    name: { pt: 'Cargas Perigosas', en: 'Hazardous', es: 'Peligrosas' },
    image: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=400&q=80',
    tons: '890t',
  },
  {
    id: 6,
    name: { pt: 'Eletrônicos', en: 'Electronics', es: 'Electrónicos' },
    image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=400&q=80',
    tons: '1.200t',
  },
];

const MerchandiseBanner = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const content = {
    pt: {
      badge: 'NOSSA EXPERTISE',
      title: 'Embarcamos mais de',
      highlight: '8.000 tipos',
      titleEnd: 'de mercadorias',
      subtitle: 'De grãos de café premium a cargas especiais, nossa experiência abrange os mais diversos segmentos do comércio internacional.',
      ctaText: 'Conheça nossos serviços',
    },
    en: {
      badge: 'OUR EXPERTISE',
      title: 'We ship over',
      highlight: '8,000 types',
      titleEnd: 'of goods',
      subtitle: 'From premium coffee beans to special cargo, our experience spans the most diverse segments of international trade.',
      ctaText: 'Discover our services',
    },
    es: {
      badge: 'NUESTRA EXPERIENCIA',
      title: 'Embarcamos más de',
      highlight: '8.000 tipos',
      titleEnd: 'de mercancías',
      subtitle: 'Desde granos de café premium hasta cargas especiales, nuestra experiencia abarca los más diversos segmentos del comercio internacional.',
      ctaText: 'Conoce nuestros servicios',
    },
  };

  const c = content[language];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-[#0f1419] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233ECDA3' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      {/* Gradient Overlays */}
      <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-[#3ECDA3]/10 to-transparent" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#3ECDA3]/5 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block bg-[#3ECDA3]/20 text-[#3ECDA3] text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-6">
            {c.badge}
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {c.title}{' '}
            <span className="text-[#3ECDA3] italic">{c.highlight}</span>
            <br />{c.titleEnd}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {c.subtitle}
          </p>
        </div>

        {/* Products Showcase */}
        <div className={`mb-16 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Product Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {products.map((product, index) => (
              <div
                key={product.id}
                className="group relative transition-all duration-500"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Card */}
                <div className="relative h-48 md:h-56 rounded-2xl overflow-hidden border-2 border-transparent transition-all duration-300">
                  {/* Image */}
                  <img
                    src={product.fallbackImage || product.image}
                    alt={product.name[language]}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src = product.fallbackImage || product.image;
                    }}
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-[#0f1419]/60 group-hover:bg-[#0f1419]/40 transition-all duration-300" />

                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-4">
                    <h3 className="text-white font-bold text-sm md:text-base">
                      {product.name[language]}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Product Navigation Dots */}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-12 transition-all duration-700 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="/servicos"
            className="inline-flex items-center gap-2 text-[#3ECDA3] font-medium hover:gap-4 transition-all duration-300"
          >
            {c.ctaText}
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default MerchandiseBanner;
